import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import * as React from "react";
import styled from "styled-components";

const enum fields {
    eras = "eras",
    accentColor = "accentColor",
    captionColor = "captionColor",
    backgroundColor = "backgroundColor",
}

interface EraFields {
    image: string;
    year: string;
    caption: string;
    lowercaseFinalS?: boolean;
}

interface EraItem extends HasFields {
    fields: EraFields;
}

interface Props extends WidgetProps {
    fields: {
        [fields.eras]: EraItem[];
        [fields.accentColor]: string;
        [fields.captionColor]: string;
        [fields.backgroundColor]: string;
    };
}

const CompanyHistoryTimeline: React.FC<Props> = ({ fields: content }) => (
    <Section backgroundColor={content.backgroundColor}>
        <EraGrid>
            {(content.eras || []).map((era, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <EraTile key={index}>
                    {era.fields.image && (
                        <EraImage src={era.fields.image} alt={era.fields.caption || era.fields.year} loading="lazy" />
                    )}
                    <ImageShade />
                    <EraCopy>
                        <Year accentColor={content.accentColor}>
                            {era.fields.lowercaseFinalS && /s$/i.test(era.fields.year) ? (
                                <>
                                    {era.fields.year.slice(0, -1)}
                                    <LowercaseSuffix>{era.fields.year.slice(-1)}</LowercaseSuffix>
                                </>
                            ) : (
                                era.fields.year
                            )}
                        </Year>
                        <Caption captionColor={content.captionColor}>{era.fields.caption}</Caption>
                    </EraCopy>
                </EraTile>
            ))}
        </EraGrid>
    </Section>
);

const definition: WidgetDefinition = {
    group: "Common",
    displayName: "Company History Timeline",
    icon: "image",
    fieldDefinitions: [
        
        {
            name: fields.accentColor,
            displayName: "Year Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#ed1c24",
            fieldType: "General",
        },
        {
            name: fields.captionColor,
            displayName: "Caption Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#ffffff",
            fieldType: "General",
        },
        {
            name: fields.backgroundColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#ffffff",
            fieldType: "General",
        },
        {
            name: fields.eras,
            displayName: "History Eras",
            editorTemplate: "ListField",
            defaultValue: [
                { fields: { image: "", year: "1937", caption: "520 W Lehigh - the original storefront" } },
                { fields: { image: "", year: "1960s", caption: "Cutting pipe by hand at 5th & Dauphin" } },
                { fields: { image: "", year: "1980s", caption: "The red-and-white fleet, Phila. PA 19133", lowercaseFinalS: true } },
                { fields: { image: "", year: "TODAY", caption: "Plymouth Meeting, PA headquarters" } },
            ],
            fieldType: "General",
            getDisplay: (item: HasFields) => item.fields.year || item.fields.caption,
            fieldDefinitions: [
                {
                    name: "image",
                    displayName: "Era Image",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                },
                {
                    name: "year",
                    displayName: "Year",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "caption",
                    displayName: "Caption",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "lowercaseFinalS",
                    displayName: "Lowercase final s",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
            ],
        }
    ],
};

const widgetModule: WidgetModule = {
    component: CompanyHistoryTimeline,
    definition,
};

export default widgetModule;

const Section = styled.section<{ backgroundColor: string }>`
    width: 100%;
    padding: 0px;
    box-sizing: border-box;
    background: ${({ backgroundColor }) => backgroundColor || "#ffffff"};
`;

const EraGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: clamp(10px, 1.4vw, 22px);

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 520px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const EraTile = styled.article`
    position: relative;
    min-width: 0;
    aspect-ratio: 0.86;
    overflow: hidden;
    background: #252525;
`;

const EraImage = styled.img`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ImageShade = styled.div`
    position: absolute;
    inset: 48% 0 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.82) 72%, rgba(0, 0, 0, 0.96) 100%);
    pointer-events: none;
`;

const EraCopy = styled.div`
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    padding: clamp(12px, 1.4vw, 20px) clamp(12px, 1.2vw, 18px) clamp(10px, 1.1vw, 16px);
    box-sizing: border-box;
`;

const Year = styled.div<{ accentColor: string }>`
    margin-bottom: clamp(8px, 1vw, 13px);
    color: ${({ accentColor }) => accentColor || "#ed1c24"};
    font-family: Impact, "Arial Narrow", sans-serif;
    font-size: clamp(25px, 3vw, 38px);
    font-weight: 400;
    line-height: 0.95;
    text-transform: uppercase;
`;

const LowercaseSuffix = styled.span`
    text-transform: lowercase;
`;

const Caption = styled.div<{ captionColor: string }>`
    color: ${({ captionColor }) => captionColor || "#ffffff"};
    font-family: Arial, sans-serif;
    font-size: clamp(12px, 1.2vw, 18px);
    font-weight: 400;
    line-height: 1.25;
    overflow-wrap: anywhere;
`;